import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from "react-router";
import React, { useEffect } from "react";

import type { Route } from "./+types/root";

import { useInitializeUser, useUserStore } from "~/lib/stores/user";
import { useNavigationMonitor } from "~/lib/useNavigationMonitor";
import "~/types/global";
import "./app.css";
import logoIco from "~/assets/logo.ico";
import { envName, isDevelopment, isProduction } from "~/lib/env";
import * as Sentry from "@sentry/react";
import { trackWebEvent } from "~/lib/web-analytics";
import { initAutocapture } from "~/lib/autocapture";
import { ConsentManager } from "~/components/features/consent/ConsentManager";
import { consentDefaultScript, gtmScript } from "~/data/gtm";

if (!isDevelopment) {
  Sentry.init({
    dsn: `https://027f21b117578db4c29e4dcec658db4d@${import.meta.env.VITE_SENTRY_DOMAIN}/2`,
    environment: envName(),
    integrations: [Sentry.browserTracingIntegration()],
    // Tracing
    tracesSampleRate: 1.0,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_PROXY_API_BASE_URL,
    ],
    // Session Replay records the user's screen (PII), so its integration is deliberately not added
    // here. These rates are read by it if and when it is added, under tracking consent. Basic error
    // monitoring is essential and stays always-on.
    sendDefaultPii: false,
    replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
  });

  // Sentry already records every fetch as a breadcrumb; the one thing it cannot see is
  // our backend's request id, which lives in a response header. Enrich its breadcrumb
  // rather than adding a second one. Registering at the fetch layer also covers requests
  // that bypass ApiClient, such as the watch feed's call to the algo gateway.
  Sentry.getClient()?.on("beforeOutgoingRequestBreadcrumb", (breadcrumb, hint) => {
    const response = (hint as { response?: Response }).response;
    const requestId = response?.headers.get("http-x-blidz-request-id");
    if (requestId && breadcrumb.data) {
      breadcrumb.data.blidz_request_id = requestId;
    }
  });
}

// Add this script to load fonts asynchronously
const fontLoadScript = `
  (function() {
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap';
    document.head.appendChild(link);
  })();
`;

export const meta: Route.MetaFunction = () => {
  return [
    {
      property: "og:image",
      content: `https://${import.meta.env.VITE_ASSET_DOMAIN}/blidz-cdn/images/common/metadata-og-image.png`,
    },
    { property: "og:title", content: "Blidz | Team Up, Save Money, Have Fun" },
    {
      property: "og:description",
      content:
        "Earn generous loyalty rewards while shopping Team-Up deals with friends, and secure deals up to 90% OFF or even free.",
    },
  ];
};

export const links: Route.LinksFunction = () => {
  const preconnectLinks: Route.LinkDescriptors = [
    // fonts
    {
      rel: "preconnect",
      href: "https://fonts.googleapis.com",
    },
    {
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
      crossOrigin: "anonymous",
    },
  ];

  // Add API base URL preconnect if available
  if (import.meta.env.VITE_API_BASE_URL) {
    preconnectLinks.push({
      rel: "preconnect",
      href: import.meta.env.VITE_API_BASE_URL,
    });
  }

  // Add CDN host preconnect if available
  if (import.meta.env.VITE_TENCENT_CDN_HOST) {
    preconnectLinks.push({
      rel: "preconnect",
      href: `https://${import.meta.env.VITE_TENCENT_CDN_HOST}`,
    });
  }

  // Add favicon links
  const faviconLinks = [
    {
      rel: "icon",
      type: "image/x-icon",
      href: logoIco,
    },
  ];

  return [...preconnectLinks, ...faviconLinks];
};

function RegionBlockedRedirect() {
  const regionBlocked = useUserStore(s => s.regionBlocked);
  const navigate = useNavigate();

  useEffect(() => {
    if (import.meta.env.VITE_ENABLE_REGION_BLOCKED_REDIRECT !== "true") return;
    if (regionBlocked) {
      navigate("/coming-soon", { replace: true });
    }
  }, [regionBlocked, navigate]);

  return null;
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const isComingSoon = pathname === "/coming-soon";
  // Initialize user authentication on app start (skip on coming-soon page to avoid API calls)
  useInitializeUser(isComingSoon);

  return (
    <html lang="en">
      <head>
        <title>Blidz</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* pinterest verification */}
        <meta name="p:domain_verify" content="d4b1a218b544b599c1af89a76579065a" />

        {/* google adsense */}
        <meta name="google-adsense-account" content="ca-pub-1006380973567470" />
        <Meta />
        <Links />

        {/*
          Consent Mode v2 defaults, then GTM (APP-1182). Document order is the guarantee that the
          denied default lands before the container loads; keep these two adjacent, in this order,
          and both INLINE — the prerenderer hoists `src` scripts above inline ones, so making either
          one external would silently break the pairing. GA4 stays cookieless until the granted
          update is sent, on consent. The GTM script defers its own container download off first
          paint (WEB-88); the denied default above still runs at parse time, before that fires.
        */}
        <script dangerouslySetInnerHTML={{ __html: consentDefaultScript }} />
        <script dangerouslySetInnerHTML={{ __html: gtmScript }} />

        {/* Google One Tap loads its script lazily off the critical path — see useGoogleOneTap (WEB-86). */}

        {/* Trustpilot Widget Script */}
        <script type="text/javascript" src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" async />

        <script dangerouslySetInnerHTML={{ __html: fontLoadScript }} />
      </head>
      <body>
        <ConsentManager>
          <main>{children}</main>
          <RegionBlockedRedirect />
        </ConsentManager>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  const location = useLocation();

  // Monitor browser navigation events across all pages
  useNavigationMonitor();

  // First-party click autocapture (runs once; not gated on consent — first-party data).
  useEffect(() => {
    initAutocapture();
  }, []);

  // First-party page_view for the web_events pipeline (SPA route changes never hit the server). Keyed
  // on pathname only: a page_view is one page (path). Query/hash-only changes (tabs, filters, anchors)
  // are the same page and would otherwise inflate route-grouped counts; those are still captured via
  // click autocapture, and each event carries the full `url` in its envelope.
  useEffect(() => {
    trackWebEvent({ event_type: "page_view", title: typeof document !== "undefined" ? document.title : "" });
  }, [location.pathname]);

  return <Outlet />;
}

export default isDevelopment ? App : Sentry.withProfiler(App);

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  // Capture error in Sentry with context
  if (!isDevelopment) {
    Sentry.captureException(error, {
      tags: {
        component: "ErrorBoundary",
        location: "root",
      },
      extra: {
        isRouteError: isRouteErrorResponse(error),
        url: window.location.href,
      },
    });
  }

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details = error.status === 404 ? "The requested page could not be found." : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="container mx-auto p-4 pt-16">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full overflow-x-auto p-4">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
